import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Map from "ol/Map.js";
import Overlay from "ol/Overlay.js";
import View from "ol/View.js";
import { easeOut } from "ol/easing.js";
import LayerGroup from "ol/layer/Group.js";
import { fromLonLat } from "ol/proj.js";
import { createPnoaLayer, PNOA_MAX_ZOOM } from "./layers/pnoa";
import { createMonitoredNetworkLayer } from "./layers/monitoredNetwork";
import {
  createFibsenSensorLayer,
  findFibsenSensorFeature,
  sensorSelectionFromFeature,
  updateFibsenSensorFeatures,
  updateFibsenSensorSelection,
} from "./layers/fibsenSensors";
import { getMonitoredNetworkExtent } from "./qgis/monitoredNetworkExtent";
import "ol/ol.css";
import "./OpenLayersMap.css";

export const MAP_LAYER_GROUP_IDS = Object.freeze({
  baseImagery: "base-imagery",
  qgisOverlays: "qgis-overlays",
  applicationVectors: "application-vectors",
});

// Converted from the UTM center previously used by the Fibsen map iframe.
export const FIBSEN_MAP_CENTER = Object.freeze([
  -0.3631133763113103,
  39.446569382103625,
]);

export const FIBSEN_MAP_ZOOM = 13.738435055749529;
export const MAP_TRANSITION_DURATION = 700;

export const MONITORED_NETWORK_FIT_OPTIONS = Object.freeze({
  padding: Object.freeze([40, 40, 40, 40]),
  maxZoom: 16,
  duration: 0,
});
export const MONITORED_NETWORK_FIT_ZOOM_REFINEMENT = 1.4;
export const SENSOR_POPUP_AUTO_PAN_OPTIONS = Object.freeze({
  margin: 24,
  animation: Object.freeze({ duration: 250 }),
});
export const SENSOR_POPUP_OFFSET = 12;
const EMPTY_SENSOR_DATA = Object.freeze([]);
const EMPTY_SENSOR_IDS = Object.freeze([]);

export const getSensorPopupPlacement = ({
  mapSize,
  sensorPixel,
  popupSize,
}) => {
  const [mapWidth, mapHeight] = Array.isArray(mapSize) ? mapSize : [];
  const [sensorX, sensorY] = Array.isArray(sensorPixel) ? sensorPixel : [];
  const [popupWidth, popupHeight] = Array.isArray(popupSize) ? popupSize : [];
  const values = [
    mapWidth,
    mapHeight,
    sensorX,
    sensorY,
    popupWidth,
    popupHeight,
  ];
  if (values.some((value) => !Number.isFinite(value) || value < 0)) {
    return "above";
  }

  const requiredSpace = popupHeight + SENSOR_POPUP_OFFSET;
  const availableAbove = sensorY - SENSOR_POPUP_AUTO_PAN_OPTIONS.margin;
  const availableBelow =
    mapHeight - sensorY - SENSOR_POPUP_AUTO_PAN_OPTIONS.margin;

  if (availableAbove >= requiredSpace) return "above";
  if (availableBelow >= requiredSpace) return "below";
  return availableBelow > availableAbove ? "below" : "above";
};

const createLayerGroup = (id, title, layers = []) => {
  const group = new LayerGroup({ layers });
  group.setProperties({ id, title });
  return group;
};

const createLayerGroups = (sensorLayer) => [
  createLayerGroup(MAP_LAYER_GROUP_IDS.baseImagery, "Base imagery", [
    createPnoaLayer(),
  ]),
  createLayerGroup(MAP_LAYER_GROUP_IDS.qgisOverlays, "QGIS overlays", [
    createMonitoredNetworkLayer(),
  ]),
  createLayerGroup(
    MAP_LAYER_GROUP_IDS.applicationVectors,
    "Application vectors",
    [sensorLayer]
  ),
];

const samePopupSensors = (currentSensors, nextSensors) =>
  currentSensors.length === nextSensors.length &&
  currentSensors.every((currentSensor, index) => {
    const nextSensor = nextSensors[index];
    return (
      currentSensor.id === nextSensor.id &&
      currentSensor.status === nextSensor.status &&
      currentSensor.lastMeasurementAt === nextSensor.lastMeasurementAt &&
      currentSensor.apiSensor === nextSensor.apiSensor &&
      currentSensor.coordinate[0] === nextSensor.coordinate[0] &&
      currentSensor.coordinate[1] === nextSensor.coordinate[1]
    );
  });

export const formatSensorMeasurementDate = (value) => {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return null;

  return new Intl.DateTimeFormat("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
    .format(date)
    .replace(",", "");
};

const POPUP_METRICS = Object.freeze([
  Object.freeze({
    code: "water_level",
    label: "Nivel de agua",
    showWhenMissing: true,
  }),
  Object.freeze({ code: "pressure", label: "Presión" }),
  Object.freeze({ code: "temperature", label: "Temperatura" }),
]);

const formatProviderName = (value) => {
  const providerName = String(value || "").trim();
  if (!providerName) return "—";
  return providerName.toLocaleLowerCase("es-ES") === "fibsen"
    ? "Fibsen"
    : providerName;
};

const latestUsableMetricMeasurement = (sensor, metricCode) => {
  const metric = (Array.isArray(sensor?.data) ? sensor.data : []).find(
    (candidate) => candidate?.category === metricCode
  );
  if (!metric) return null;

  const usable = (Array.isArray(metric.measurements)
    ? metric.measurements
    : []
  )
    .map((measurement) => {
      const rawValue = measurement?.value;
      return {
        ...measurement,
        numericValue:
          rawValue === null || rawValue === undefined || rawValue === ""
            ? NaN
            : Number(rawValue),
        timestamp: new Date(measurement?.date).getTime(),
      };
    })
    .filter(
      (measurement) =>
        Number.isFinite(measurement.numericValue) &&
        Number.isFinite(measurement.timestamp)
    )
    .sort((left, right) => right.timestamp - left.timestamp);

  if (usable.length === 0) return null;
  return {
    value: usable[0].numericValue,
    unit: usable[0].unit || metric.unit || "",
  };
};

const formatMetricMeasurement = (measurement) => {
  if (!measurement) return "—";
  const value = new Intl.NumberFormat("es-ES", {
    maximumFractionDigits: 6,
  }).format(measurement.value);
  return measurement.unit ? `${value} ${measurement.unit}` : value;
};

const SensorPopupOverlay = ({ map, sensor, onClose }) => {
  const [coordinateX, coordinateY] = sensor.coordinate;
  const [element] = useState(() => {
    const popupElement = document.createElement("div");
    popupElement.className = "sensor-map-popup";
    popupElement.style.visibility = "hidden";
    return popupElement;
  });

  useEffect(() => {
    element.style.visibility = "hidden";
    const overlay = new Overlay({
      element,
      positioning: "bottom-center",
      offset: [0, -SENSOR_POPUP_OFFSET],
      stopEvent: true,
      autoPan: SENSOR_POPUP_AUTO_PAN_OPTIONS,
    });
    overlay.setPosition([coordinateX, coordinateY]);
    map.addOverlay(overlay);

    let disposed = false;
    let placementFrameId;
    const resizeFrameId = window.requestAnimationFrame(() => {
      if (disposed) return;
      map.updateSize();
      placementFrameId = window.requestAnimationFrame(() => {
        if (disposed) return;
        const mapSize = map.getSize?.();
        const sensorPixel = map.getPixelFromCoordinate?.([
          coordinateX,
          coordinateY,
        ]);
        const popupRect = element.getBoundingClientRect();
        const placement = getSensorPopupPlacement({
          mapSize,
          sensorPixel,
          popupSize: [popupRect.width, popupRect.height],
        });
        const below = placement === "below";

        overlay.setPositioning(below ? "top-center" : "bottom-center");
        overlay.setOffset([
          0,
          below ? SENSOR_POPUP_OFFSET : -SENSOR_POPUP_OFFSET,
        ]);
        element.classList.toggle("sensor-map-popup--below", below);
        overlay.panIntoView(SENSOR_POPUP_AUTO_PAN_OPTIONS);
        element.style.visibility = "visible";
      });
    });

    return () => {
      disposed = true;
      window.cancelAnimationFrame(resizeFrameId);
      if (placementFrameId !== undefined) {
        window.cancelAnimationFrame(placementFrameId);
      }
      overlay.setPosition(undefined);
      map.removeOverlay(overlay);
    };
  }, [coordinateX, coordinateY, element, map, sensor]);

  const formattedMeasurement = sensor.lastMeasurementAt
    ? formatSensorMeasurementDate(sensor.lastMeasurementAt)
    : null;
  const formattedCommunication = sensor.lastCommunicationAt
    ? formatSensorMeasurementDate(sensor.lastCommunicationAt)
    : null;
  const sensorName = String(sensor.name || "").trim();
  const showSensorName = sensorName && sensorName !== sensor.code;
  const statusLabel =
    sensor.status === "online" ? "En línea" : "Fuera de línea";

  return createPortal(
    <div data-testid="sensor-map-popup" data-sensor-id={sensor.id}>
      <button
        type="button"
        className="sensor-map-popup__close"
        aria-label={`Cerrar información de ${sensor.code}`}
        onClick={() => onClose(sensor.id)}
      >
        ×
      </button>
      <div className="sensor-map-popup__code">{sensor.code}</div>
      {showSensorName && <div>Sensor: {sensorName}</div>}
      <div>Estado: {statusLabel}</div>
      <div className="sensor-map-popup__section">
        <div>Proveedor: {formatProviderName(sensor.providerName)}</div>
        <div>Ubicación: {sensor.locationName || "—"}</div>
        <div>Última comunicación: {formattedCommunication || "—"}</div>
        {formattedMeasurement && (
          <div>Última medición: {formattedMeasurement}</div>
        )}
      </div>
      <div className="sensor-map-popup__section">
        {POPUP_METRICS.map(({ code, label, showWhenMissing = false }) => {
          const measurement = latestUsableMetricMeasurement(sensor, code);
          if (!measurement && !showWhenMissing) return null;
          return (
            <div key={code}>
              {label}: {formatMetricMeasurement(measurement)}
            </div>
          );
        })}
      </div>
    </div>,
    element
  );
};

const OpenLayersMap = ({
  className = "",
  initialCenter = FIBSEN_MAP_CENTER,
  initialZoom = FIBSEN_MAP_ZOOM,
  allowInitialRouteFit = true,
  sensorData = EMPTY_SENSOR_DATA,
  selectedSensorIds = EMPTY_SENSOR_IDS,
  focusedSensorId = null,
  sensorFocusZoom = 17,
  onSensorSelect,
  onSensorDeselect,
  onClearSensorSelection,
  onMapReady,
}) => {
  const targetRef = useRef(null);
  const mapRef = useRef(null);
  const sensorLayerRef = useRef(null);
  const routeExtentRef = useRef(null);
  const onMapReadyRef = useRef(onMapReady);
  const onSensorSelectRef = useRef(onSensorSelect);
  const onSensorDeselectRef = useRef(onSensorDeselect);
  const onClearSensorSelectionRef = useRef(onClearSensorSelection);
  const sensorDataRef = useRef(sensorData);
  const selectedSensorIdsRef = useRef(selectedSensorIds);
  const focusedSensorIdRef = useRef(focusedSensorId);
  const allowInitialRouteFitRef = useRef(allowInitialRouteFit);
  const hasUserInteractedRef = useRef(false);
  const pendingOverviewRestoreRef = useRef(false);
  const previousSelectedSensorIdsRef = useRef(selectedSensorIds);
  const [mapInstance, setMapInstance] = useState(null);
  const [popupSensors, setPopupSensors] = useState([]);
  const [longitude, latitude] = initialCenter;

  allowInitialRouteFitRef.current = allowInitialRouteFit;
  sensorDataRef.current = sensorData;
  selectedSensorIdsRef.current = selectedSensorIds;
  focusedSensorIdRef.current = focusedSensorId;

  useEffect(() => {
    onMapReadyRef.current = onMapReady;
  }, [onMapReady]);

  useEffect(() => {
    onSensorSelectRef.current = onSensorSelect;
  }, [onSensorSelect]);

  useEffect(() => {
    onSensorDeselectRef.current = onSensorDeselect;
  }, [onSensorDeselect]);

  useEffect(() => {
    onClearSensorSelectionRef.current = onClearSensorSelection;
  }, [onClearSensorSelection]);

  const syncSensorPopups = useCallback((sensorLayer, sensorIds) => {
    const nextPopupSensors = (Array.isArray(sensorIds) ? sensorIds : [])
      .map((sensorId) => {
        const feature = findFibsenSensorFeature(sensorLayer, sensorId);
        const selection = sensorSelectionFromFeature(feature);
        const geometry = feature?.getGeometry?.() || feature?.get?.("geometry");
        const coordinate = geometry?.getCoordinates?.();
        return selection && coordinate
          ? { ...selection, coordinate: [...coordinate] }
          : null;
      })
      .filter(Boolean);

    setPopupSensors((currentSensors) =>
      samePopupSensors(currentSensors, nextPopupSensors)
        ? currentSensors
        : nextPopupSensors
    );
  }, []);

  const animateRouteOverview = useCallback((map, extent) => {
    if (!map || !extent) return;
    const view = map.getView();
    const startCenter = view.getCenter();
    const startZoom = view.getZoom();

    view.cancelAnimations();
    view.fit(extent, {
      ...MONITORED_NETWORK_FIT_OPTIONS,
      padding: [...MONITORED_NETWORK_FIT_OPTIONS.padding],
    });

    const fittedCenter = view.getCenter();
    const fittedZoom = view.getZoom();
    const targetZoom = Number.isFinite(fittedZoom)
      ? Math.min(
          fittedZoom + MONITORED_NETWORK_FIT_ZOOM_REFINEMENT,
          MONITORED_NETWORK_FIT_OPTIONS.maxZoom
        )
      : fittedZoom;

    view.setCenter(startCenter);
    view.setZoom(startZoom);
    view.cancelAnimations();
    view.animate({
      center: fittedCenter,
      zoom: targetZoom,
      duration: MAP_TRANSITION_DURATION,
      easing: easeOut,
    });
  }, []);

  const restoreRouteOverview = useCallback(() => {
    if (!mapRef.current || !routeExtentRef.current) {
      pendingOverviewRestoreRef.current = true;
      return;
    }

    pendingOverviewRestoreRef.current = false;
    animateRouteOverview(mapRef.current, routeExtentRef.current);
  }, [animateRouteOverview]);

  useEffect(() => {
    updateFibsenSensorFeatures(
      sensorLayerRef.current,
      sensorData,
      selectedSensorIdsRef.current
    );
    syncSensorPopups(sensorLayerRef.current, selectedSensorIdsRef.current);
  }, [sensorData, syncSensorPopups]);

  useEffect(() => {
    updateFibsenSensorSelection(sensorLayerRef.current, selectedSensorIds);
    syncSensorPopups(sensorLayerRef.current, selectedSensorIds);

    const hadSelection = previousSelectedSensorIdsRef.current.length > 0;
    if (hadSelection && selectedSensorIds.length === 0) {
      restoreRouteOverview();
    }
    previousSelectedSensorIdsRef.current = selectedSensorIds;
  }, [restoreRouteOverview, selectedSensorIds, syncSensorPopups]);

  useEffect(() => {
    if (!mapInstance || !focusedSensorId) return;
    const feature = findFibsenSensorFeature(
      sensorLayerRef.current,
      focusedSensorId
    );
    const coordinate = feature?.getGeometry?.()?.getCoordinates?.();
    if (!coordinate) return;

    const view = mapInstance.getView();
    view.cancelAnimations();
    view.animate({
      center: coordinate,
      zoom: sensorFocusZoom,
      duration: MAP_TRANSITION_DURATION,
      easing: easeOut,
    });
  }, [focusedSensorId, mapInstance, sensorFocusZoom]);

  useEffect(() => {
    if (!targetRef.current || mapRef.current) return undefined;

    const sensorLayer = createFibsenSensorLayer({
      apiSensors: sensorDataRef.current,
      selectedSensorIds: selectedSensorIdsRef.current,
    });
    sensorLayerRef.current = sensorLayer;

    const map = new Map({
      target: targetRef.current,
      layers: createLayerGroups(sensorLayer),
      view: new View({
        projection: "EPSG:3857",
        center: fromLonLat([longitude, latitude], "EPSG:3857"),
        zoom: initialZoom,
        maxZoom: PNOA_MAX_ZOOM,
      }),
    });

    mapRef.current = map;
    setMapInstance(map);
    onMapReadyRef.current?.(map);
    syncSensorPopups(sensorLayer, selectedSensorIdsRef.current);

    const markUserInteraction = () => {
      hasUserInteractedRef.current = true;
    };
    const target = targetRef.current;
    target.addEventListener("pointerdown", markUserInteraction);
    target.addEventListener("wheel", markUserInteraction);
    target.addEventListener("keydown", markUserInteraction);

    const handleSensorClick = (event) => {
      const feature = map.forEachFeatureAtPixel(
        event.pixel,
        (candidate) => candidate,
        {
          hitTolerance: 6,
          layerFilter: (layer) => layer === sensorLayer,
        }
      );
      const selection = sensorSelectionFromFeature(feature);
      if (!selection) {
        pendingOverviewRestoreRef.current = true;
        onClearSensorSelectionRef.current?.();
        return;
      }

      hasUserInteractedRef.current = true;
      onSensorSelectRef.current?.(selection);
    };
    map.on("singleclick", handleSensorClick);

    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(() => map.updateSize());

    resizeObserver?.observe(targetRef.current);
    map.updateSize();

    let disposed = false;
    getMonitoredNetworkExtent()
      .then((extent) => {
        if (disposed || !extent) return;
        routeExtentRef.current = extent;

        if (
          pendingOverviewRestoreRef.current &&
          selectedSensorIdsRef.current.length === 0
        ) {
          pendingOverviewRestoreRef.current = false;
          animateRouteOverview(map, extent);
          return;
        }

        if (
          !allowInitialRouteFitRef.current ||
          hasUserInteractedRef.current ||
          selectedSensorIdsRef.current.length > 0 ||
          focusedSensorIdRef.current
        ) {
          return;
        }

        animateRouteOverview(map, extent);
      })
      .catch(() => {
        // The existing fallback center/zoom remains usable when capabilities
        // are unavailable or do not describe the monitored network.
      });

    return () => {
      disposed = true;
      target.removeEventListener("pointerdown", markUserInteraction);
      target.removeEventListener("wheel", markUserInteraction);
      target.removeEventListener("keydown", markUserInteraction);
      map.un("singleclick", handleSensorClick);
      resizeObserver?.disconnect();
      map.setTarget(undefined);
      map.dispose();
      mapRef.current = null;
      sensorLayerRef.current = null;
      routeExtentRef.current = null;
    };
  }, [animateRouteOverview, initialZoom, latitude, longitude, syncSensorPopups]);

  const handlePopupClose = useCallback((sensorId) => {
    onSensorDeselectRef.current?.(sensorId);
  }, []);

  return (
    <div className={`openlayers-map ${className}`.trim()}>
      <div
        ref={targetRef}
        className="openlayers-map__target"
        role="region"
        aria-label="Mapa GIS de HortaTech"
        tabIndex={0}
        data-projection="EPSG:3857"
      />
      {mapInstance &&
        popupSensors.map((sensor) => (
          <SensorPopupOverlay
            key={sensor.id}
            map={mapInstance}
            sensor={sensor}
            onClose={handlePopupClose}
          />
        ))}
    </div>
  );
};

export default OpenLayersMap;
